import {
  DEFAULT_APP_CONFIG,
  mockAllyTurn,
  type AllyTurnRequest,
  type AllyTurnResponse,
  type AstroSummary,
  type CompatibilityDoc,
  type ConnectionDoc,
  type ConnectionIntent,
  type ConversationDoc,
  type CreditLedgerEntry,
  type CreditSource,
  type DiscoveryCard,
  type DiscoveryFeedResult,
  type EntitlementDoc,
  type MediaRef,
  type MemoryItem,
  type MessageDoc,
  type RecommendationDoc,
  type ReportReason,
  type UserDoc,
  COMPATIBILITY_DIMENSIONS,
  ENTITLEMENTS,
} from '@astrally/shared';
import {
  buildRationale,
  computeCompatibility,
  gradesSnapshot,
  rankCandidates,
  type MatchableProfile,
} from '@astrally/matching';
import { localdb } from '../localdb';
import type {
  Backend,
  CompatibilityView,
  EntitlementSnapshot,
  FeedPage,
  FeedPost,
  FeedComment,
  FeedCommentsPage,
  FeedCommentReceipt,
  FeedLikeResult,
} from './types';
import { DEMO_PEOPLE, DEMO_SELF_UID } from './demoSeed';

/**
 * DemoBackend — the complete commercial product with zero credentials.
 * [MOCKED INTEGRATION] Billing/AI are deterministic mocks; matching,
 * compatibility, quotas, the credit ledger and the connection state machine
 * are the REAL engines/invariants running locally against IndexedDB.
 */
export class DemoBackend implements Backend {
  readonly kind = 'demo' as const;
  private uid: string | null = null;
  private cfg = DEFAULT_APP_CONFIG;
  /** Monotonic suffix — Date.now() alone collides inside one millisecond. */
  private seq = 0;

  private nextId(prefix: string): string {
    return `${prefix}-${Date.now()}-${(this.seq++).toString(36).padStart(4, '0')}`;
  }

  async init(): Promise<void> {
    const seeded = await localdb.get<boolean>('kv', 'demoSeeded');
    if (!seeded) {
      for (const p of DEMO_PEOPLE) {
        await localdb.set('demoUsers', p.user.uid, p);
      }
      await localdb.set('kv', 'demoSeeded', true);
    }
    const session = await localdb.get<{ uid: string }>('kv', 'session');
    this.uid = session?.uid ?? null;
  }

  currentUid(): string | null {
    return this.uid;
  }

  async signIn(): Promise<string> {
    return this.signUp();
  }

  async signUp(): Promise<string> {
    this.uid = DEMO_SELF_UID;
    await localdb.set('kv', 'session', { uid: this.uid });
    return this.uid;
  }

  async signOut(): Promise<void> {
    this.uid = null;
    await localdb.del('kv', 'session');
  }

  private req(): string {
    if (!this.uid) throw new Error('Not signed in');
    return this.uid;
  }

  private dayKey(): string {
    return new Date().toISOString().slice(0, 10).replace(/-/g, '');
  }

  // --- entitlements / credits ----------------------------------------------

  private emptyEnt(uid: string): EntitlementDoc {
    return {
      uid,
      tier: 'none',
      baseEntitled: false,
      proStatus: 'none',
      baseOutgoingUsed: 0,
      aiCreditBalance: 0,
      updatedAtISO: new Date().toISOString(),
    };
  }

  private async ent(): Promise<EntitlementDoc> {
    const uid = this.req();
    // Seeded demo people are treated as Base-entitled counterparties.
    if (uid !== DEMO_SELF_UID) {
      const stored = await localdb.get<EntitlementDoc>('kv', `ent:${uid}`);
      return stored ?? { ...this.emptyEnt(uid), tier: 'base', baseEntitled: true };
    }
    return (await localdb.get<EntitlementDoc>('kv', `ent:${uid}`)) ?? this.emptyEnt(uid);
  }

  private async saveEnt(ent: EntitlementDoc): Promise<void> {
    await localdb.set('kv', `ent:${ent.uid}`, { ...ent, updatedAtISO: new Date().toISOString() });
  }

  private tierOf(ent: EntitlementDoc): 'none' | 'base' | 'pro' {
    if (ent.proStatus === 'active' && ent.proExpiresAtISO && ent.proExpiresAtISO > new Date().toISOString()) {
      return 'pro';
    }
    return ent.baseEntitled ? 'base' : 'none';
  }

  private async requireBase(): Promise<EntitlementDoc> {
    const ent = await this.ent();
    if (this.tierOf(ent) === 'none') {
      throw new Error('base-required: unlock Astrally with the one-time Base purchase.');
    }
    return ent;
  }

  private async ledger(uid: string): Promise<CreditLedgerEntry[]> {
    return (await localdb.get<CreditLedgerEntry[]>('kv', `ledger:${uid}`)) ?? [];
  }

  private async appendLedger(
    ent: EntitlementDoc,
    type: 'credit' | 'debit',
    source: CreditSource,
    amount: number,
    referenceId?: string,
  ): Promise<EntitlementDoc> {
    const uid = ent.uid;
    const entries = await this.ledger(uid);
    const balanceAfter = type === 'credit' ? ent.aiCreditBalance + amount : ent.aiCreditBalance - amount;
    if (balanceAfter < 0) {
      throw new Error(`insufficient-credits: this needs ${amount} credits; you have ${ent.aiCreditBalance}.`);
    }
    entries.unshift({
      id: `led-${Date.now()}-${entries.length}`,
      uid,
      type,
      source,
      amount,
      balanceAfter,
      referenceId,
      createdAtISO: new Date().toISOString(),
    });
    await localdb.set('kv', `ledger:${uid}`, entries.slice(0, 200));
    const updated = { ...ent, aiCreditBalance: balanceAfter };
    await this.saveEnt(updated);
    return updated;
  }

  private quotaOf(ent: EntitlementDoc): { remaining: number; kind: 'base-lifetime' | 'pro-daily' } {
    if (this.tierOf(ent) === 'pro') {
      const used = ent.proDailyKey === this.dayKey() ? (ent.proDailyUsed ?? 0) : 0;
      return { remaining: Math.max(0, this.cfg.pro.dailyOutgoingRequests - used), kind: 'pro-daily' };
    }
    return {
      remaining: Math.max(0, this.cfg.base.lifetimeOutgoingRequests - ent.baseOutgoingUsed),
      kind: 'base-lifetime',
    };
  }

  async getEntitlement(): Promise<EntitlementSnapshot> {
    const ent = await this.ent();
    return {
      entitlement: ent,
      tier: this.tierOf(ent),
      quota: this.quotaOf(ent),
      creditBalance: ent.aiCreditBalance,
      config: {
        base: this.cfg.base,
        pro: {
          products: this.cfg.pro.products,
          priceDisplay: this.cfg.pro.priceDisplay,
          dailyOutgoingRequests: this.cfg.pro.dailyOutgoingRequests,
          discoveryDailyCards: this.cfg.pro.discoveryDailyCards,
          fairUse: this.cfg.pro.fairUse,
        },
        creditCosts: this.cfg.creditCosts,
        creditPacks: this.cfg.creditPacks,
      },
    };
  }

  async purchase(productId: string, _platform?: 'play' | 'mock' | 'dev'): Promise<{ granted: string }> {
    let ent = await this.ent();
    if (productId === this.cfg.base.productId) {
      if (!ent.baseEntitled) {
        ent = { ...ent, baseEntitled: true, tier: 'base', basePurchasedAtISO: new Date().toISOString() };
        await this.saveEnt(ent);
        if (this.cfg.base.introductoryCredits > 0) {
          await this.appendLedger(ent, 'credit', 'introductory', this.cfg.base.introductoryCredits, 'base-purchase');
        }
      }
      return { granted: 'base' };
    }
    if (productId === this.cfg.pro.products.monthly || productId === this.cfg.pro.products.annual) {
      const days = productId === this.cfg.pro.products.annual ? 365 : 30;
      ent = {
        ...ent,
        baseEntitled: true,
        tier: 'pro',
        proStatus: 'active',
        proProductId: productId,
        proExpiresAtISO: new Date(Date.now() + days * 86400000).toISOString(),
      };
      await this.saveEnt(ent);
      return { granted: productId.includes('annual') ? 'pro-annual' : 'pro-monthly' };
    }
    const pack = this.cfg.creditPacks.find((p) => p.id === productId);
    if (pack) {
      await this.appendLedger(ent, 'credit', 'purchase', pack.credits, productId);
      return { granted: 'credits' };
    }
    throw new Error(`Unknown productId ${productId}`);
  }

  async listLedger(): Promise<CreditLedgerEntry[]> {
    return this.ledger(this.req());
  }

  // --- users ---------------------------------------------------------------

  async getUser(uid: string): Promise<UserDoc | null> {
    if (uid === DEMO_SELF_UID) {
      return (await localdb.get<UserDoc>('kv', 'selfUser')) ?? null;
    }
    const p = await localdb.get<{ user: UserDoc }>('demoUsers', uid);
    return p?.user ?? null;
  }

  async saveUser(user: UserDoc): Promise<void> {
    if (user.uid === DEMO_SELF_UID) {
      await localdb.set('kv', 'selfUser', user);
    } else {
      const p = await localdb.get<Record<string, unknown>>('demoUsers', user.uid);
      await localdb.set('demoUsers', user.uid, { ...(p ?? {}), user });
    }
  }

  async saveAstroSummary(summary: AstroSummary): Promise<void> {
    await localdb.set('kv', `astro:${this.req()}`, summary);
  }

  async pauseRecommendations(paused: boolean): Promise<void> {
    const user = await this.getUser(this.req());
    if (user) {
      await this.saveUser({
        ...user,
        recommendationsPaused: paused,
        discoverable: user.socialEnabled === true && !paused,
      });
    }
  }

  /** Mirrors socialSetEnabled: the master opt-in, private by default. */
  async setSocialEnabled(enabled: boolean, intents?: ConnectionIntent[]): Promise<void> {
    const uid = this.req();
    const user = await this.getUser(uid);
    if (!user) throw new Error('Complete onboarding first');
    if (enabled) {
      await this.requireBase();
      const clean = intents ?? user.intents ?? [];
      if (clean.length === 0) {
        throw new Error('Choose at least one kind of connection you are open to.');
      }
      await this.saveUser({
        ...user,
        socialEnabled: true,
        socialConsentAtISO: user.socialConsentAtISO ?? new Date().toISOString(),
        intents: clean,
        primaryIntention: user.primaryIntention ?? clean[0],
        discoverable: true,
        recommendationsPaused: false,
      });
      return;
    }
    await this.saveUser({ ...user, socialEnabled: false, discoverable: false });
    // Withdraw pending requests in both directions.
    for (const conn of await this.allConnections()) {
      if (conn.status !== 'pending') continue;
      await localdb.set('demoIntroductions', conn.id, {
        ...conn,
        status: conn.requesterUid === uid ? 'withdrawn' : 'expired',
        respondedAtISO: new Date().toISOString(),
      });
    }
    for (const rec of await this.listRecommendations()) {
      await localdb.del('demoRecommendations', `${uid}:${rec.id}`);
    }
  }

  // --- Astrally AI ----------------------------------------------------------

  async allyTurn(request: AllyTurnRequest): Promise<AllyTurnResponse> {
    let ent = await this.requireBase();
    const modelClass = request.modelClass ?? 'standard';
    let creditCost = 0;
    if (modelClass !== 'standard') {
      const generationId = request.generationId ?? `gen-${Date.now()}`;
      // Mirrors the server reservation: a replayed generationId returns the
      // stored result instead of charging (and generating) again.
      const key = `gen:${this.req()}:${generationId}`;
      const prior = await localdb.get<{ reply: string; citedFactIds: string[]; creditCost: number }>(
        'kv',
        key,
      );
      if (prior) {
        return {
          reply: prior.reply,
          citedFactIds: prior.citedFactIds,
          memorySuggestions: [],
          provider: 'mock',
          creditCost: prior.creditCost,
          creditBalance: ent.aiCreditBalance,
        };
      }
      creditCost =
        modelClass === 'deeper' ? this.cfg.creditCosts.deeperAnswer : this.cfg.creditCosts.masterReading;
      ent = await this.appendLedger(
        ent,
        'debit',
        modelClass === 'deeper' ? 'deeper_answer' : 'master_reading',
        creditCost,
        generationId,
      );
      const generated = mockAllyTurn(request);
      await localdb.set('kv', key, {
        reply: generated.reply,
        citedFactIds: generated.citedFactIds,
        creditCost,
      });
      for (const s of generated.memorySuggestions) {
        const id = `m${Date.now()}-${(this.seq++).toString(36)}`;
        await localdb.set('demoMemories', `${this.req()}:${id}`, {
          id,
          category: s.category,
          content: s.content,
          status: 'suggested',
          source: s.confidence >= 0.85 ? 'user-stated' : 'inferred',
          confidence: s.confidence,
          sensitive: s.sensitive,
          cloudApproved: false,
          createdAtISO: new Date().toISOString(),
          updatedAtISO: new Date().toISOString(),
        } satisfies MemoryItem);
      }
      return { ...generated, creditCost, creditBalance: ent.aiCreditBalance };
    }
    const response = mockAllyTurn(request);
    const uid = this.req();
    for (const s of response.memorySuggestions) {
      const id = `m${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const item: MemoryItem = {
        id,
        category: s.category,
        content: s.content,
        status: 'suggested',
        source: s.confidence >= 0.85 ? 'user-stated' : 'inferred',
        confidence: s.confidence,
        sensitive: s.sensitive,
        cloudApproved: false,
        createdAtISO: new Date().toISOString(),
        updatedAtISO: new Date().toISOString(),
      };
      await localdb.set('demoMemories', `${uid}:${id}`, item);
    }
    return response;
  }

  // --- memories -------------------------------------------------------------

  async listMemories(): Promise<MemoryItem[]> {
    const uid = this.req();
    const keys = await localdb.keys('demoMemories');
    const items: MemoryItem[] = [];
    for (const k of keys) {
      if (!k.startsWith(`${uid}:`)) continue;
      const item = await localdb.get<MemoryItem>('demoMemories', k);
      if (item) items.push(item);
    }
    return items.sort((a, b) => b.createdAtISO.localeCompare(a.createdAtISO));
  }

  async addMemory(item: MemoryItem): Promise<void> {
    await localdb.set('demoMemories', `${this.req()}:${item.id}`, item);
  }

  async resolveMemory(
    memoryId: string,
    action: 'confirm' | 'reject' | 'delete' | 'edit',
    content?: string,
    cloudApproved?: boolean,
  ): Promise<void> {
    const key = `${this.req()}:${memoryId}`;
    const item = await localdb.get<MemoryItem>('demoMemories', key);
    if (!item) return;
    if (action === 'delete') {
      await localdb.del('demoMemories', key);
      return;
    }
    await localdb.set('demoMemories', key, {
      ...item,
      status: action === 'reject' ? 'rejected' : 'confirmed',
      content: action === 'edit' && content ? content : item.content,
      cloudApproved: action === 'confirm' ? cloudApproved === true : item.cloudApproved,
      updatedAtISO: new Date().toISOString(),
    });
  }

  // --- profiles for matching -------------------------------------------------

  private async profileOf(uid: string): Promise<MatchableProfile | null> {
    if (uid === DEMO_SELF_UID || uid === this.uid) {
      const user = await this.getUser(uid);
      if (!user) return null;
      const astro = await localdb.get<AstroSummary>('kv', `astro:${uid}`);
      return {
        uid,
        displayName: user.displayName,
        ageBand: user.ageBand,
        region: user.region,
        city: user.city,
        languages: user.languages,
        intents: user.intents,
        interests: user.interests,
        lifeStage: user.lifeStage,
        dimensions: {
          communicationDirectness: 0.65,
          conversationDepth: 0.6,
          humour: 0.55,
          emotionalOpenness: 0.6,
          socialEnergy: 0.55,
          replyConsistency: 0.7,
          profileCompleteness: 0.85,
          reliability: 0.75,
        },
        trustLevel: 0.95,
        astro,
        discoverable: user.discoverable,
        recommendationsPaused: user.recommendationsPaused,
        ageConfirmed: user.ageConfirmed,
      };
    }
    const p = await localdb.get<(typeof DEMO_PEOPLE)[number]>('demoUsers', uid);
    if (!p) return null;
    return {
      uid,
      displayName: p.user.displayName,
      ageBand: p.user.ageBand,
      region: p.user.region,
      city: p.user.city,
      languages: p.user.languages,
      intents: p.user.intents,
      interests: p.user.interests,
      lifeStage: p.user.lifeStage,
      dimensions: p.scores,
      trustLevel: p.trustLevel,
      astro: p.astro,
      discoverable: p.user.discoverable,
      recommendationsPaused: p.user.recommendationsPaused,
      ageConfirmed: p.user.ageConfirmed,
    };
  }

  // --- Ally-surfaced recommendations ----------------------------------------

  async generateRecommendations(): Promise<number> {
    const uid = this.req();
    if (this.tierOf(await this.ent()) === 'none') return 0;
    const seeker = await this.profileOf(uid);
    if (!seeker) return 0;

    const blocked = new Set((await localdb.get<string[]>('kv', `blocks:${uid}`)) ?? []);
    const existing = await this.listRecommendations();
    const exclude = new Set(existing.map((r) => r.candidateUid));
    for (const conn of await this.allConnections()) {
      if (conn.status === 'pending' || conn.status === 'accepted' || conn.status === 'blocked') {
        exclude.add(conn.requesterUid === uid ? conn.recipientUid : conn.requesterUid);
      }
    }

    const poolUids = (await localdb.keys('demoUsers')).filter((k) => k !== uid);
    const pool: MatchableProfile[] = [];
    for (const p of poolUids) {
      const prof = await this.profileOf(p);
      if (prof) pool.push(prof);
    }

    const active = existing.filter((r) => ['pending', 'seen'].includes(r.status)).length;
    const capacity = Math.max(0, 3 - active);
    if (capacity === 0) return 0;

    const { top } = rankCandidates({
      seeker,
      pool,
      ctx: { blockedUids: blocked, excludeUids: exclude },
      limit: capacity,
    });

    const now = new Date().toISOString();
    for (const sc of top) {
      const id = `rec-${sc.candidate.uid}`;
      const rec: RecommendationDoc = {
        id,
        forUid: uid,
        candidateUid: sc.candidate.uid,
        pairId: sc.pair.pairId,
        scoringVersion: ENTITLEMENTS.scoringVersion,
        grades: gradesSnapshot(sc),
        sharedInterests: sc.pair.extras.sharedInterestList,
        matchedIntents: sc.pair.extras.sharedIntents,
        rationale: buildRationale(sc),
        status: 'pending',
        createdAtISO: now,
        updatedAtISO: now,
      };
      await localdb.set('demoRecommendations', `${uid}:${id}`, rec);
    }
    return top.length;
  }

  async listRecommendations(): Promise<RecommendationDoc[]> {
    const uid = this.req();
    const keys = (await localdb.keys('demoRecommendations')).filter((k) => k.startsWith(`${uid}:`));
    const out: RecommendationDoc[] = [];
    for (const k of keys) {
      const r = await localdb.get<RecommendationDoc>('demoRecommendations', k);
      if (r) out.push(r);
    }
    return out.sort((a, b) => b.createdAtISO.localeCompare(a.createdAtISO));
  }

  async setRecommendationStatus(
    recId: string,
    status: 'seen' | 'dismissed' | 'not-similar',
  ): Promise<void> {
    const key = `${this.req()}:${recId}`;
    const rec = await localdb.get<RecommendationDoc>('demoRecommendations', key);
    if (rec) {
      await localdb.set('demoRecommendations', key, {
        ...rec,
        status,
        updatedAtISO: new Date().toISOString(),
      });
    }
  }

  // --- discovery ------------------------------------------------------------

  private async compat(seeker: MatchableProfile, candidate: MatchableProfile): Promise<CompatibilityDoc> {
    const fresh = computeCompatibility(seeker, candidate, this.cfg.compatibilityVersion);
    const cached = await localdb.get<CompatibilityDoc>('kv', `compat:${fresh.pairId}`);
    if (cached && cached.scoreVersion === fresh.scoreVersion && cached.inputsHash === fresh.inputsHash) {
      return cached;
    }
    const doc: CompatibilityDoc = { ...fresh, calculatedAtISO: new Date().toISOString() };
    await localdb.set('kv', `compat:${fresh.pairId}`, doc);
    return doc;
  }

  async discoveryFeed(): Promise<DiscoveryFeedResult> {
    const uid = this.req();
    const ent = await this.requireBase();
    const me = await this.getUser(uid);
    if (me?.socialEnabled !== true) {
      throw new Error('social-disabled: connections are off for this account.');
    }
    const seeker = await this.profileOf(uid);
    if (!seeker) return { cards: [], remainingRequests: 0, requestQuotaKind: 'base-lifetime' };

    const blocked = new Set((await localdb.get<string[]>('kv', `blocks:${uid}`)) ?? []);
    const passed = (await localdb.get<Record<string, string>>('kv', `passed:${uid}`)) ?? {};
    const excluded = new Set<string>([uid, ...blocked]);
    const passMs = this.cfg.connection.passReappearDays * 86400000;
    for (const [pUid, iso] of Object.entries(passed)) {
      if (Date.now() - Date.parse(iso) < passMs) excluded.add(pUid);
    }
    for (const conn of await this.allConnections()) {
      if (['pending', 'accepted', 'blocked'].includes(conn.status)) {
        excluded.add(conn.requesterUid === uid ? conn.recipientUid : conn.requesterUid);
      }
    }

    const w = this.cfg.discoveryWeights;
    const scored: { card: DiscoveryCard; rank: number }[] = [];
    for (const candidateUid of await localdb.keys('demoUsers')) {
      if (excluded.has(candidateUid)) continue;
      const profile = await this.profileOf(candidateUid);
      const user = await this.getUser(candidateUid);
      if (!profile || !user || !user.discoverable) continue;
      const compat = await this.compat(seeker, profile);
      const sharedIntent = seeker.intents.some((i) => user.intents.includes(i));
      const rank =
        w.compatibility * (compat.overall / 100) +
        w.intentMatch * (sharedIntent ? 1 : 0) +
        w.geo * (seeker.city === user.city ? 1 : 0.6) +
        w.activity * 0.8 +
        w.profileQuality * (user.profileCompletion ?? 0.7) +
        w.verification * (user.verification?.photo ? 1 : 0);
      const topDims = [...COMPATIBILITY_DIMENSIONS]
        .sort((a, b) => compat.dimensions[b] - compat.dimensions[a])
        .slice(0, 2)
        .map((d) => `${d} ${compat.dimensions[d]}`);
      scored.push({
        rank,
        card: {
          uid: candidateUid,
          displayName: user.displayName,
          age: user.age,
          city: user.city,
          avatarPath: user.avatar?.path,
          bio: user.bio,
          primaryIntention: user.primaryIntention ?? user.intents[0],
          interests: user.interests.slice(0, 4),
          compatibility: { overall: compat.overall, highlights: topDims },
          // Demo parity with discoveryFns: omitted, never zeroed, when the
          // seeded profile has no usable Moon nakshatra.
          ...(compat.kundli ? { kundliScore: compat.kundli } : {}),
          verified: Boolean(user.verification?.photo),
          activeBand: 'today',
        },
      });
    }
    scored.sort((a, b) => b.rank - a.rank);
    const budget =
      this.tierOf(ent) === 'pro' ? this.cfg.pro.discoveryDailyCards : this.cfg.base.discoveryDailyCards;
    const quota = this.quotaOf(ent);
    return {
      cards: scored.slice(0, budget).map((s) => s.card),
      remainingRequests: quota.remaining,
      requestQuotaKind: quota.kind,
    };
  }

  async discoveryAction(targetUid: string, action: 'pass' | 'save' | 'unsave'): Promise<void> {
    const uid = this.req();
    if (action === 'pass') {
      const passed = (await localdb.get<Record<string, string>>('kv', `passed:${uid}`)) ?? {};
      passed[targetUid] = new Date().toISOString();
      await localdb.set('kv', `passed:${uid}`, passed);
    } else {
      const saved = new Set((await localdb.get<string[]>('kv', `saved:${uid}`)) ?? []);
      if (action === 'save') saved.add(targetUid);
      else saved.delete(targetUid);
      await localdb.set('kv', `saved:${uid}`, [...saved]);
    }
  }

  async compatibilityGet(targetUid: string): Promise<CompatibilityView> {
    const ent = await this.requireBase();
    const [seeker, candidate] = await Promise.all([
      this.profileOf(this.req()),
      this.profileOf(targetUid),
    ]);
    if (!seeker || !candidate) throw new Error('Profile not found');
    const compat = await this.compat(seeker, candidate);
    if (this.tierOf(ent) === 'pro') return { full: true, compatibility: compat };
    return {
      full: false,
      compatibility: {
        pairId: compat.pairId,
        userA: compat.userA,
        userB: compat.userB,
        overall: compat.overall,
        highlights: compat.highlights,
        scoreVersion: compat.scoreVersion,
      },
      upgradeHint: 'Astrally Pro unlocks the complete compatibility breakdown.',
    };
  }

  // --- connections ----------------------------------------------------------

  private async allConnections(): Promise<ConnectionDoc[]> {
    const uid = this.req();
    const out: ConnectionDoc[] = [];
    for (const k of await localdb.keys('demoIntroductions')) {
      const c = await localdb.get<ConnectionDoc>('demoIntroductions', k);
      if (c && (c.requesterUid === uid || c.recipientUid === uid)) out.push(c);
    }
    return out;
  }

  async connectionRequest(
    recipientUid: string,
    introMessage?: string,
  ): Promise<{ connectionId: string; quota: { remaining: number; kind: string } }> {
    const uid = this.req();
    const ent = await this.requireBase();
    const me = await this.getUser(uid);
    if (me?.socialEnabled !== true) {
      throw new Error('social-disabled: turn connections on before sending requests.');
    }
    const pairId = [uid, recipientUid].sort().join('_');

    for (const k of await localdb.keys('demoIntroductions')) {
      const c = await localdb.get<ConnectionDoc>('demoIntroductions', k);
      if (!c || c.pairId !== pairId) continue;
      if (c.status === 'pending') throw new Error('A request between you is already pending.');
      if (c.status === 'accepted') throw new Error('You are already connected.');
      if (c.status === 'blocked') throw new Error('Connection not available.');
      if (c.status === 'declined' && c.respondedAtISO) {
        const cool = Date.parse(c.respondedAtISO) + this.cfg.connection.declinedCooldownDays * 86400000;
        if (Date.now() < cool) throw new Error('Connection not available right now.');
      }
    }

    // Quota consumption — the REAL invariant, enforced locally in demo.
    const tier = this.tierOf(ent);
    if (tier === 'pro') {
      const key = this.dayKey();
      const used = ent.proDailyKey === key ? (ent.proDailyUsed ?? 0) : 0;
      if (used >= this.cfg.pro.dailyOutgoingRequests) {
        throw new Error("quota-exhausted-pro-daily: today's connection requests are used.");
      }
      await this.saveEnt({ ...ent, proDailyKey: key, proDailyUsed: used + 1 });
    } else {
      if (ent.baseOutgoingUsed >= this.cfg.base.lifetimeOutgoingRequests) {
        throw new Error(
          'quota-exhausted-base-lifetime: all lifetime connection requests are used. Astrally Pro adds a daily allowance.',
        );
      }
      await this.saveEnt({ ...ent, baseOutgoingUsed: ent.baseOutgoingUsed + 1 });
    }

    const id = this.nextId('conn');
    const conn: ConnectionDoc = {
      id,
      pairId,
      requesterUid: uid,
      recipientUid,
      status: 'pending',
      introMessage: introMessage?.trim() ? introMessage.trim().slice(0, 280) : undefined,
      createdAtISO: new Date().toISOString(),
      expiresAtISO: new Date(Date.now() + this.cfg.connection.pendingExpiryDays * 86400000).toISOString(),
    };
    await localdb.set('demoIntroductions', id, conn);
    const after = await this.ent();
    return { connectionId: id, quota: this.quotaOf(after) };
  }

  async connectionRespond(
    connectionId: string,
    accept: boolean,
  ): Promise<{ status: string; conversationId?: string }> {
    const uid = this.req();
    const conn = await localdb.get<ConnectionDoc>('demoIntroductions', connectionId);
    if (!conn) throw new Error('Request not found');
    if (conn.recipientUid !== uid) throw new Error('Only the recipient can respond');
    if (conn.status !== 'pending') throw new Error(`Request already ${conn.status}`);

    if (!accept) {
      await localdb.set('demoIntroductions', connectionId, {
        ...conn,
        status: 'declined',
        respondedAtISO: new Date().toISOString(),
      });
      return { status: 'declined' };
    }
    const convId = `conv-${conn.pairId}`;
    const conversation: ConversationDoc = {
      id: convId,
      members: [conn.requesterUid, conn.recipientUid].sort(),
      introductionId: connectionId,
      status: 'active',
      createdAtISO: new Date().toISOString(),
      unread: { [conn.requesterUid]: 0, [conn.recipientUid]: 0 },
    };
    await localdb.set('demoConversations', convId, conversation);
    await localdb.set('demoIntroductions', connectionId, {
      ...conn,
      status: 'accepted',
      respondedAtISO: new Date().toISOString(),
      conversationId: convId,
    });
    return { status: 'accepted', conversationId: convId };
  }

  async connectionWithdraw(connectionId: string): Promise<void> {
    const uid = this.req();
    const conn = await localdb.get<ConnectionDoc>('demoIntroductions', connectionId);
    if (!conn || conn.requesterUid !== uid || conn.status !== 'pending') {
      throw new Error('Cannot withdraw this request');
    }
    // No refund — a request is consumed when sent.
    await localdb.set('demoIntroductions', connectionId, {
      ...conn,
      status: 'withdrawn',
      respondedAtISO: new Date().toISOString(),
    });
  }

  async listConnections(): Promise<{ incoming: ConnectionDoc[]; outgoing: ConnectionDoc[] }> {
    const uid = this.req();
    const all = await this.allConnections();
    return {
      incoming: all.filter((c) => c.recipientUid === uid && c.status === 'pending'),
      outgoing: all.filter((c) => c.requesterUid === uid),
    };
  }

  // --- messaging ------------------------------------------------------------

  async listConversations(): Promise<ConversationDoc[]> {
    const uid = this.req();
    const out: ConversationDoc[] = [];
    for (const k of await localdb.keys('demoConversations')) {
      const c = await localdb.get<ConversationDoc>('demoConversations', k);
      if (c && c.members.includes(uid)) out.push(c);
    }
    return out.sort((a, b) =>
      (b.lastMessageAtISO ?? b.createdAtISO).localeCompare(a.lastMessageAtISO ?? a.createdAtISO),
    );
  }

  async listMessages(conversationId: string): Promise<MessageDoc[]> {
    const uid = this.req();
    const keys = (await localdb.keys('demoMessages')).filter((k) => k.startsWith(`${conversationId}:`));
    const out: MessageDoc[] = [];
    for (const k of keys) {
      const m = await localdb.get<MessageDoc>('demoMessages', k);
      if (m && !(m.deletedFor ?? []).includes(uid)) out.push(m);
    }
    return out.sort(
      (a, b) => a.sentAtISO.localeCompare(b.sentAtISO) || a.id.localeCompare(b.id),
    );
  }

  /**
   * Demo mode never calls a moderation vendor, so every demo message is
   * recorded `unchecked` — the same marker the server uses when nothing
   * inspected the text. Claiming 'clean' would be a lie in the one place
   * where it is cheapest to tell one.
   *
   * There is no fake blocklist here on purpose. Demo mode runs entirely on
   * this device against seeded people; there is no stranger to protect, and a
   * hand-rolled keyword filter would teach users (and us) the wrong thing
   * about what the real check does.
   */
  async sendMessage(conversationId: string, text: string, replyTo?: string): Promise<void> {
    const uid = this.req();
    const conv = await localdb.get<ConversationDoc>('demoConversations', conversationId);
    if (!conv || conv.status !== 'active') throw new Error('Conversation is not active');
    if (!conv.members.includes(uid)) throw new Error('Not a member');
    const id = this.nextId('msg');
    const msg: MessageDoc = {
      id,
      senderUid: uid,
      text,
      sentAtISO: new Date().toISOString(),
      deliveredTo: [],
      readBy: [uid],
      moderation: 'unchecked',
      replyTo,
    };
    await localdb.set('demoMessages', `${conversationId}:${id}`, msg);
    await localdb.set('demoConversations', conversationId, {
      ...conv,
      lastMessageAtISO: msg.sentAtISO,
      lastMessagePreview: text.slice(0, 80),
    });
  }

  private async updateMessage(
    conversationId: string,
    messageId: string,
    fn: (m: MessageDoc) => MessageDoc,
  ): Promise<void> {
    const key = `${conversationId}:${messageId}`;
    const m = await localdb.get<MessageDoc>('demoMessages', key);
    if (m) await localdb.set('demoMessages', key, fn(m));
  }

  async reactToMessage(conversationId: string, messageId: string, emoji: string | null): Promise<void> {
    const uid = this.req();
    await this.updateMessage(conversationId, messageId, (m) => {
      const reactions = { ...(m.reactions ?? {}) };
      if (emoji) reactions[uid] = emoji;
      else delete reactions[uid];
      return { ...m, reactions };
    });
  }

  async editMessage(conversationId: string, messageId: string, text: string): Promise<void> {
    const uid = this.req();
    await this.updateMessage(conversationId, messageId, (m) => {
      if (m.senderUid !== uid) throw new Error('Only the sender can edit');
      return { ...m, text, editedAtISO: new Date().toISOString() };
    });
  }

  async deleteMessageForSelf(conversationId: string, messageId: string): Promise<void> {
    const uid = this.req();
    await this.updateMessage(conversationId, messageId, (m) => ({
      ...m,
      deletedFor: [...new Set([...(m.deletedFor ?? []), uid])],
    }));
  }

  async endConversation(conversationId: string): Promise<void> {
    const conv = await localdb.get<ConversationDoc>('demoConversations', conversationId);
    if (conv) await localdb.set('demoConversations', conversationId, { ...conv, status: 'ended' });
  }

  // --- safety ---------------------------------------------------------------

  async blockUser(targetUid: string): Promise<void> {
    const uid = this.req();
    const blocks = (await localdb.get<string[]>('kv', `blocks:${uid}`)) ?? [];
    if (!blocks.includes(targetUid)) blocks.push(targetUid);
    await localdb.set('kv', `blocks:${uid}`, blocks);
    for (const conv of await this.listConversations()) {
      if (conv.members.includes(targetUid)) {
        await localdb.set('demoConversations', conv.id, { ...conv, status: 'blocked' });
      }
    }
    for (const conn of await this.allConnections()) {
      if (conn.pairId.includes(targetUid) && ['pending', 'accepted'].includes(conn.status)) {
        await localdb.set('demoIntroductions', conn.id, {
          ...conn,
          status: 'blocked',
          respondedAtISO: new Date().toISOString(),
        });
      }
    }
    for (const rec of await this.listRecommendations()) {
      if (rec.candidateUid === targetUid) {
        await localdb.set('demoRecommendations', `${uid}:${rec.id}`, {
          ...rec,
          status: 'blocked',
          updatedAtISO: new Date().toISOString(),
        });
      }
    }
  }

  async reportUser(targetUid: string, reason: ReportReason, context?: string): Promise<void> {
    const reports = (await localdb.get<Record<string, unknown>[]>('kv', 'reports')) ?? [];
    reports.push({
      reporterUid: this.req(),
      subjectUid: targetUid,
      reason,
      context,
      createdAtISO: new Date().toISOString(),
    });
    await localdb.set('kv', 'reports', reports);
  }

  // --- the members-only feed ------------------------------------------------

  /* Demo mode has NO SEEDED POSTS, on purpose.

     The seeded demo people are fictional profiles the matching engine runs
     against; writing words into their mouths would be a different thing —
     invented opinions attributed to invented people, shown in a surface whose
     entire premise is that real members wrote what you are reading. So the
     demo feed starts genuinely empty, which also means the empty state (the
     thing hardest to get right and easiest to never see) is what a demo user
     meets first. Their own posts appear normally.

     Everything is kept under one `kv` key rather than a store of its own:
     adding an IndexedDB object store needs a DB_VERSION bump in
     app/src/lib/localdb.ts, which was outside this change's file scope. */
  private async posts(): Promise<FeedPost[]> {
    return (await localdb.get<FeedPost[]>('kv', 'feedPosts')) ?? [];
  }

  async listFeed(opts?: { cursor?: string; limit?: number }): Promise<FeedPage> {
    const uid = this.req();
    const self = await this.getUser(uid);
    if (self?.socialEnabled !== true) {
      throw new Error('The feed is part of connections, which are switched off for your account.');
    }
    const blocks = (await localdb.get<string[]>('kv', `blocks:${uid}`)) ?? [];
    const limit = Math.min(opts?.limit ?? 15, 30);
    const all = (await this.posts())
      .filter((p) => !blocks.includes(p.authorUid))
      .sort((a, b) => b.sortKey.localeCompare(a.sortKey));
    const start = opts?.cursor ? all.findIndex((p) => p.sortKey === opts.cursor) + 1 : 0;
    const page = all.slice(start, start + limit);

    // Engagement is derived here rather than read off the stored post — see
    // the note above the like/comment helpers below.
    const likes = await this.likes();
    const comments = await this.comments();
    return {
      posts: page.map((p) => ({
        ...p,
        mine: p.authorUid === uid,
        likeCount: (likes[p.id] ?? []).length,
        commentCount: (comments[p.id] ?? []).length,
        likedByMe: (likes[p.id] ?? []).includes(uid),
      })),
      cursor: start + limit < all.length ? (page[page.length - 1]?.sortKey ?? null) : null,
    };
  }

  /**
   * Demo mode never calls a moderation vendor, so every demo post is recorded
   * `unchecked` — exactly as demo messages are, and for the same reason:
   * claiming 'clean' would be a lie in the one place where it is cheapest to
   * tell one.
   */
  async createPost(text: string, media?: MediaRef): Promise<FeedPost> {
    const uid = this.req();
    const self = await this.getUser(uid);
    if (self?.socialEnabled !== true) {
      throw new Error('Turn connections on before posting.');
    }
    const trimmed = text.trim();
    if (!trimmed) throw new Error('Write something first.');
    const createdAtISO = new Date().toISOString();
    const id = this.nextId('post');
    const post: FeedPost = {
      id,
      authorUid: uid,
      authorDisplayName: self.displayName,
      text: trimmed.slice(0, 500),
      createdAtISO,
      sortKey: `${createdAtISO}#${id}`,
      moderation: 'unchecked',
      mine: true,
      likeCount: 0,
      commentCount: 0,
      likedByMe: false,
      ...(media ? { media, mediaModeration: 'unchecked' as const } : {}),
    };
    await localdb.set('kv', 'feedPosts', [post, ...(await this.posts())].slice(0, 200));
    return post;
  }

  async deletePost(postId: string): Promise<void> {
    const uid = this.req();
    const all = await this.posts();
    const post = all.find((p) => p.id === postId);
    if (!post || post.authorUid !== uid) throw new Error('That post isn’t available.');
    await localdb.set(
      'kv',
      'feedPosts',
      all.filter((p) => p.id !== postId),
    );
    // The likes and comments go with it. Nothing would read them once the post
    // is gone, but leaving them is leaving other people's words on this device
    // after the thing they were written under stopped existing.
    const { [postId]: _droppedLikes, ...likes } = await this.likes();
    const { [postId]: _droppedComments, ...comments } = await this.comments();
    await localdb.set('kv', 'feedLikes', likes);
    await localdb.set('kv', 'feedComments', comments);
  }

  async reportPost(postId: string, reason: ReportReason, context?: string): Promise<void> {
    const post = (await this.posts()).find((p) => p.id === postId);
    if (!post) throw new Error('That post is no longer there.');
    await this.reportUser(post.authorUid, reason, context);
  }

  /* --- feed engagement --------------------------------------------------------

     COUNTS ARE DERIVED, NOT STORED. The server keeps `likeCount` and
     `commentCount` on the post document because deriving them there would mean
     counting a subcollection on every read; here the whole feed is three
     values in IndexedDB, so counting is free — and a derived count cannot
     drift from the thing it counts, which is the entire class of bug the
     server spends a transaction to avoid. Demo mode therefore exercises the
     same UI states without reimplementing the transaction.

     Likes: postId -> the uids that liked it. The uid appearing at most once in
     that array is what makes a demo like idempotent, exactly as the document
     id being the uid does on the server. */
  private async likes(): Promise<Record<string, string[]>> {
    return (await localdb.get<Record<string, string[]>>('kv', 'feedLikes')) ?? {};
  }

  private async comments(): Promise<Record<string, FeedComment[]>> {
    return (await localdb.get<Record<string, FeedComment[]>>('kv', 'feedComments')) ?? {};
  }

  async toggleLike(postId: string, on: boolean): Promise<FeedLikeResult> {
    const uid = this.req();
    const post = (await this.posts()).find((p) => p.id === postId);
    if (!post) throw new Error('That post is no longer there.');

    const all = await this.likes();
    const current = all[postId] ?? [];
    // Set semantics in both directions: liking twice is one like, unliking
    // what was never liked changes nothing and is not an error.
    const next = on
      ? current.includes(uid)
        ? current
        : [...current, uid]
      : current.filter((u) => u !== uid);

    await localdb.set('kv', 'feedLikes', { ...all, [postId]: next });
    return { liked: next.includes(uid), likeCount: next.length };
  }

  /**
   * Demo mode never calls a moderation vendor, so every demo comment is
   * recorded `unchecked` — the same honesty demo posts and demo messages
   * apply, for the same reason: 'clean' would claim an inspection that did not
   * happen, and this is the cheapest possible place to tell that lie.
   */
  async addComment(postId: string, text: string): Promise<FeedCommentReceipt> {
    const uid = this.req();
    const self = await this.getUser(uid);
    if (self?.socialEnabled !== true) {
      throw new Error('The feed is part of connections, which are switched off for your account.');
    }
    const post = (await this.posts()).find((p) => p.id === postId);
    if (!post) throw new Error('That post is no longer there.');

    const trimmed = text.trim();
    if (!trimmed) throw new Error('Write something first.');

    const id = this.nextId('comment');
    const comment: FeedComment = {
      id,
      uid,
      authorName: self.displayName,
      text: trimmed.slice(0, 500),
      createdAtISO: new Date().toISOString(),
      moderation: 'unchecked',
      mine: true,
      canDelete: true,
    };
    const all = await this.comments();
    await localdb.set('kv', 'feedComments', {
      ...all,
      [postId]: [...(all[postId] ?? []), comment],
    });
    return { commentId: id, moderation: comment.moderation };
  }

  async deleteComment(postId: string, commentId: string): Promise<void> {
    const uid = this.req();
    const post = (await this.posts()).find((p) => p.id === postId);
    const all = await this.comments();
    const list = all[postId] ?? [];
    const comment = list.find((c) => c.id === commentId);
    // Author or post owner — and one refusal for "no such comment" and "not
    // yours" alike, so the demo cannot teach a different lesson from the
    // server about what an id probe reveals.
    if (!post || !comment || (comment.uid !== uid && post.authorUid !== uid)) {
      throw new Error('That comment isn’t available.');
    }
    await localdb.set('kv', 'feedComments', {
      ...all,
      [postId]: list.filter((c) => c.id !== commentId),
    });
  }

  async listComments(postId: string, cursor?: string): Promise<FeedCommentsPage> {
    const uid = this.req();
    const post = (await this.posts()).find((p) => p.id === postId);
    if (!post) throw new Error('That post is no longer there.');

    /* Newest first, ordered by (createdAtISO desc, id desc) — THE SAME
       COMPOUND THE SERVER USES, and the id half is not decoration. Two
       comments posted in the same millisecond tie on the timestamp, and a
       stable sort then leaves them in insertion order, which is oldest-first:
       exactly backwards, and only ever visible to someone typing fast. The
       demo ids carry a monotonic sequence, so `id desc` breaks the tie the way
       the server's `__name__ desc` does.

       `mine` / `canDelete` are recomputed for whoever is asking rather than
       trusted from the stored row — demo accounts are switchable, so the
       values frozen at write time belong to a different person now. */
    const ordered = [...((await this.comments())[postId] ?? [])]
      .sort((a, b) => b.createdAtISO.localeCompare(a.createdAtISO) || b.id.localeCompare(a.id))
      .map((c) => ({ ...c, mine: c.uid === uid, canDelete: c.uid === uid || post.authorUid === uid }));

    const start = cursor ? ordered.findIndex((c) => c.id === cursor) + 1 : 0;
    const page = ordered.slice(start, start + 20);
    return {
      comments: page,
      cursor: start + 20 < ordered.length ? (page[page.length - 1]?.id ?? null) : null,
    };
  }

  /**
   * Demo mode has no cloud storage and no Storage rules, and the paywall this
   * feature rests on IS a Storage rule. Faking an upload here would produce a
   * demo where media "works" and a real build where the same code path is
   * governed by something the demo never exercised — the worst kind of parity.
   * So it refuses, in words a user can act on.
   */
  async uploadPostMedia(): Promise<MediaRef> {
    throw new Error(
      'Demo mode keeps everything on this device and has nowhere to store a photo or video. Sign in to a real account to post one.',
    );
  }

  async loadMedia(): Promise<Blob> {
    throw new Error('There is no stored media in demo mode.');
  }

  async deleteAccount(): Promise<void> {
    await localdb.wipeEverything();
    this.uid = null;
  }

  async exportData(): Promise<Record<string, unknown>> {
    const uid = this.req();
    return {
      user: await this.getUser(uid),
      astroSummary: await localdb.get('kv', `astro:${uid}`),
      birthProfile: await localdb.get('kv', 'birthProfile'),
      entitlement: await this.ent(),
      creditLedger: await this.ledger(uid),
      memories: await this.listMemories(),
      recommendations: await this.listRecommendations(),
      connections: await this.allConnections(),
      conversations: await this.listConversations(),
      feedPosts: (await this.posts()).filter((p) => p.authorUid === uid),
      note: 'Demo-mode export. Exact birth data and charts never left this device.',
    };
  }

  async switchDemoUser(uid: string): Promise<void> {
    this.uid = uid;
    await localdb.set('kv', 'session', { uid });
  }

  async listDemoUsers(): Promise<UserDoc[]> {
    const out: UserDoc[] = [];
    const self = await localdb.get<UserDoc>('kv', 'selfUser');
    if (self) out.push(self);
    for (const k of await localdb.keys('demoUsers')) {
      const p = await localdb.get<{ user: UserDoc }>('demoUsers', k);
      if (p) out.push(p.user);
    }
    return out;
  }
}
